import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { MenuItem } from 'primeng/api';
import { TableLazyLoadEvent, TableRowReorderEvent } from 'primeng/table';
import { Subscription } from 'rxjs';
import { MenuButtonColumn } from '../model';
import { ColumnTypeEnum } from '../model/base-column/column-type-enum';
import { IColumn } from '../model/base-column/column.model';
import { TableConfig } from '../model/config/table-config.model';
import { HyperlinkColumn } from '../model/hyperlink-column/hyperlink-column.model';

export interface EoTableReorderItem {
  id: string;
  sort: number;
}
export interface EoTableReorderEvent {
  items: EoTableReorderItem[];
}

@Component({
  selector: 'eo-table',
  templateUrl: './eo-table.component.html',
  styleUrl: './eo-table.component.scss',
})
export class EoTableComponent<T> {
  @Input() panelStyleClass = 'container';
  @Input() headerStyleClass?: string;
  @Input() customTemplates: { [key: string]: TemplateRef<any> | null } = {};
  @Input() lazyLoadOnInit = true;
  @Input() lazy: boolean = false;
  @Input() paginator: boolean = false;
  @Input() rows: number = 10;
  @Input() totalRecords?: number;
  @Input() loading = false;
  @Input() Config: TableConfig<T> | undefined;

  _config?: TableConfig<T>;
  @Input() set config(val: TableConfig<T> | undefined) {
    this.Config = val;
    this.applyConfigOptions();
    this.setResults();
  }

  get config(): TableConfig<T> | undefined {
    return this.Config;
  }

  _rowData: T[] | undefined;
  @Input() set rowData(val: T[] | undefined) {
    this._rowData = val;

    if (this.rowData && this.Config) {
      this.Config = {
        ...this.Config,
        Data: this.rowData ?? [],
      };
    }
    this.applyConfigOptions();
    this.setResults();
  }
  get rowData(): T[] | undefined {
    return this._rowData ?? this.Config?.Data;
  }

  @Input() dataKey: string = '';
  @Input() currentPage: number | undefined;
  @Input() pageSize: number | undefined;
  @Input() totalCount: number | undefined;
  @Input() tableStyle: any = { 'min-width': '50rem', 'border-radius': '10px' };
  @Input() styleClass: string = 'p-datatable-sm p-datatable-striped';
  @Input() rowsPerPageOptions = [5, 10, 20, 30, 50, 100];
  @Input() isTableVisible: boolean = true;
  @Input() customSort: boolean = false;
  @Input() sortMode: 'single' | 'multiple' = 'single';
  @Input() sortField?: string;
  @Input() resetPageOnSort: boolean = true;
  @Output() onSort: EventEmitter<any> = new EventEmitter();
  @Output() pageChange = new EventEmitter<any>();
  @Output() lazyLoad = new EventEmitter<any>();
  @Output() view: EventEmitter<any> = new EventEmitter();
  @Output() edit: EventEmitter<any> = new EventEmitter();
  @Input() enableRowReorder: boolean = false;
  @Input() rowIdField: keyof T | string = 'id';
  @Input() sortFieldName: keyof T | string = 'sort';
  @Output() rowReorderChange = new EventEmitter<EoTableReorderEvent>();

  ColumnTypeEnum = ColumnTypeEnum;

  constructor() {}
  ngOnInit() {
    if (this.rowData && this.Config) {
      this.Config = {
        ...this.Config,
        Data: this.rowData ?? [],
      };
    }
    this.applyConfigOptions();
    this.setResults();
  }

  private dataSubscription: Subscription | undefined;
  ngOnDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
  }

  //lazy load
  onLazyLoad(event: TableLazyLoadEvent) {
    this.lazyLoad.emit(event);
  }

  // Pagination
  onPageChange(event: any) {
    // if (event?.page === this.currentPage && event?.items === this.pageSize)
    //   return;

    if (!this.lazy) {
      this.setCurrentPage(event.page);
      this.setPagesize(event.rows);
    }

    this.pageChange.emit(event);
  }

  private hasSortingEnabled(): boolean {
    const colSort = this.Config?.Columns?.some((c) => c?.GetSort?.()) ?? false;
    return !!(this.sortField || this.customSort || colSort);
  }

  get canReorder(): boolean {
    const configReorder = this.Config?.reorder === true;
    return (
      (this.enableRowReorder || configReorder) && !this.hasSortingEnabled()
    );
  }

  private applyConfigOptions() {
    const config = this.Config;

    if (this.enableRowReorder === false && config?.reorder === true) {
      this.enableRowReorder = true;
    }

    if (
      (!this.rowIdField || this.rowIdField === 'id') &&
      config?.reorderIdField
    ) {
      this.rowIdField = config.reorderIdField;
    }

    if (
      (!this.sortFieldName || this.sortFieldName === 'sort') &&
      config?.reorderSortField
    ) {
      this.sortFieldName = config.reorderSortField;
    }
  }

  onRowReorder(event: TableRowReorderEvent | any) {
    const sf = this.sortFieldName ?? 'sort';
    const idf = this.rowIdField ?? 'id';

    const page = this.getCurrentPage();
    const size = this.getPagesize();
    const hasPaging = !!this.Config?.Paging;

    const from = hasPaging ? page * size + event.dragIndex : event.dragIndex;
    const to = hasPaging ? page * size + event.dropIndex : event.dropIndex;

    const list = [...this.currentData];
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    this.currentData = list;

    const eventMinIndex = Math.min(event.dropIndex, event.dragIndex);
    const eventMaxIndex = Math.max(event.dropIndex, event.dragIndex);

    const globalStart = hasPaging ? page * size + eventMinIndex : eventMinIndex;

    const affectedSlice = this.currentData.slice(
      globalStart,
      globalStart + (eventMaxIndex - eventMinIndex + 1)
    );

    const payloadPage = affectedSlice.map((item, i) => ({
      id: (item as any)[idf],
      sort: globalStart + i + 1,
    }));

    const eventData: EoTableReorderEvent = { items: payloadPage };

    this.rowReorderChange.emit(eventData);
    this.Config?.onReorder?.(eventData);
  }

  setCurrentPage(value: number) {
    this.currentPage = value;
  }
  getCurrentPage() {
    return this.currentPage ?? 0;
  }

  setPagesize(value: number) {
    this.pageSize = value;
  }
  getPagesize() {
    return this.pageSize ?? 10;
  }

  private currentData: T[] = [];
  setResults() {
    if (this.Config?.Data) {
      this.currentData = this.Config?.Data;
    } else {
      this.dataSubscription = this.Config?.BehaviorData?.subscribe((data) => {
        this.currentData = data;
      });
    }
  }

  getResults() {
    if (this.Config?.Paging) {
      const currentPage = this.getCurrentPage();
      const pageSize = this.getPagesize();

      return (
        this.currentData.slice(
          currentPage * pageSize,
          (currentPage + 1) * pageSize
        ) ?? []
      );
    }
    return this.currentData ?? [];
  }

  getTotal(): number {
    const length = this.totalRecords ?? this.Config?.Data?.length ?? 0;
    return length;
  }

  getFirst() {
    if (this.currentPage && this.pageSize) {
      return this.currentPage * this.pageSize;
    }

    const currentPage = this.getCurrentPage();
    const pageSize = this.getPagesize();
    return currentPage * pageSize;
  }

  getIcon(
    icon: string | ((item?: T) => string) | undefined,
    item?: any
  ): string | undefined {
    if (typeof icon === 'function') {
      return icon(item);
    } else {
      return icon;
    }
  }

  getHeaderIcon(icon: string | (() => string) | undefined): string | undefined {
    if (typeof icon === 'function') {
      return icon();
    } else {
      return icon;
    }
  }

  getTitle(
    title: string | ((item?: T) => string) | undefined,
    item?: any
  ): string | undefined {
    if (typeof title === 'function') {
      return title(item);
    } else {
      return title;
    }
  }

  getHeaderTitle(
    title: string | (() => string) | undefined
  ): string | undefined {
    if (typeof title === 'function') {
      return title();
    } else {
      return title;
    }
  }

  getTooltip(
    tooltip: string | ((item?: any) => string) | undefined,
    item?: any
  ): string | undefined {
    if (typeof tooltip === 'function') {
      return tooltip(item);
    } else {
      return tooltip;
    }
  }

  getStyleClass(
    styleClass: string | ((item?: T) => string) | undefined,
    item?: any
  ): string | undefined {
    if (typeof styleClass === 'function') {
      return styleClass(item);
    } else {
      return styleClass;
    }
  }

  getValue(
    value: string | ((item?: T) => string) | undefined,
    item?: any
  ): string | undefined {
    if (typeof value === 'function') {
      return value(item) ?? '-';
    } else {
      return item ?? '-';
    }
  }

  protected getLoadingColumns() {
    let colLength = this.config?.Columns?.length ?? 0;

    if (this.config?.LineNumbers) colLength++;
    return Array.from(Array(colLength).keys());
  }

  getFileUrl(column: IColumn<T>): string {
    return (column as HyperlinkColumn<T>)?.getFileUrlProp() ?? '';
  }

  GetColumnProp(column: IColumn<T>, prop: string): any {
    return (column as any)[prop];
  }

  getMenuItems(column: IColumn<T>, row: T): MenuItem[] {
    if (column instanceof MenuButtonColumn) return column.getPMenuItems(row);
    return [];
  }

  hasVisibleMenuItems(column: IColumn<T>, row: T): boolean {
    if (column instanceof MenuButtonColumn) {
      const items = column.getPMenuItems(row);
      return items.find((i) => i.visible === true) !== undefined;
    }

    return false;
  }

  getMenuItemss(): MenuItem[] {
    return [
      {
        label: 'Editt',
        icon: 'pi pi-pencil',
        command: () => console.log('edit'),
      },
      {
        label: 'Deletet',
        icon: 'pi pi-trash',
        command: () => console.log('delete'),
      },
    ];
  }

  onSortFunction(event: any) {
    this.onSort.emit(event);
  }

  isVisible(col: IColumn<T>): boolean {
    if (col?.visible === undefined) return true;
    if (typeof col.visible === 'function') return col.visible();
    return col.visible;
  }
}
